import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService } from '../finance/finance-api.service';
import {
  CreditDecisionRecord,
  EvaluateCreditPayload,
  SalesApiService,
  SalesChannelRecord
} from './sales-api.service';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';

@Component({
  standalone: true,
  selector: 'ain-sales-credit-decisions',
  imports: [
    DatePipe, AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputNumberModule,
    SelectModule, CustomerSelectorComponent, CurrencySelectorComponent, SalesAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="t('sales.credit.decisions.title')" [description]="t('sales.credit.decisions.description')">
      <section class="card evaluate">
        <h3>{{ t('sales.credit.evaluate.title') }}</h3>
        <div class="grid">
          <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="evaluateForm.customerAccountId" /></label>
          <label><span>{{ t('sales.orders.fields.channel') }}</span><p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="evaluateForm.salesChannelId" [showClear]="true" appendTo="body" /></label>
          <label><span>{{ t('sales.credit.fields.currency') }}</span><ain-currency-selector [(ngModel)]="evaluateForm.currencyCode" /></label>
          <label><span>{{ t('sales.credit.evaluate.proposedAmount') }}</span><p-inputNumber [(ngModel)]="evaluateForm.proposedAmount" [min]="0" /></label>
          <div class="action"><button pButton type="button" [label]="t('sales.credit.evaluate.run')" [disabled]="evaluating()" (click)="runEvaluation()"></button></div>
        </div>
        @if (lastResult()) {
          <p class="result" [class.denied]="!lastResult()!.allowed">
            {{ lastResult()!.allowed ? t('sales.credit.evaluate.allowed') : t('sales.credit.evaluate.denied') }}
            — {{ t('sales.credit.evaluate.available') }}: {{ lastResult()!.availableCredit }}
          </p>
        }
        @if (evaluateError()) { <p class="error">{{ evaluateError() }}</p> }
        @if (evaluateForm.customerAccountId) {
          <ain-sales-ai-copilot-panel entityType="CreditEvaluation" [entityId]="evaluateForm.customerAccountId" defaultRecommendationType="NextBestAction" />
        }
      </section>

      <div class="list-header">
        <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="filterCustomerId" (ngModelChange)="loadDecisions()" /></label>
        <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="loadDecisions()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <p-table [value]="decisions()" dataKey="id" [paginator]="true" [rows]="20">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('sales.credit.decisions.evaluatedAt') }}</th>
              <th>{{ t('sales.orders.fields.customer') }}</th>
              <th>{{ t('sales.credit.evaluate.proposedAmount') }}</th>
              <th>{{ t('sales.credit.evaluate.available') }}</th>
              <th>{{ t('columns.status') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.evaluatedAtUtc | date:'short' }}</td>
              <td>{{ customerLabel(row.customerAccountId) }}</td>
              <td>{{ row.proposedAmount }}</td>
              <td>{{ row.availableCredit }}</td>
              <td>{{ row.allowed ? t('sales.credit.evaluate.allowed') : t('sales.credit.evaluate.denied') }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (listError()) { <p class="error">{{ listError() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 12px; }
    .evaluate h3 { margin: 0 0 10px; font-size: 15px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .action { display: flex; align-items: end; }
    .result { margin: 10px 0 0; font-weight: 600; color: #047857; }
    .result.denied { color: #b91c1c; }
    .list-header { display: flex; gap: 12px; align-items: end; margin-bottom: 10px; flex-wrap: wrap; }
    .list-header label { display: grid; gap: 6px; min-width: 240px; }
    .error { color: #b91c1c; margin: 8px 0 0; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesCreditDecisionsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly customers = signal<Map<string, string>>(new Map());
  readonly evaluating = signal(false);
  readonly listError = signal('');
  readonly evaluateError = signal('');
  readonly decisions = signal<CreditDecisionRecord[]>([]);
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly lastResult = signal<{ allowed: boolean; availableCredit: number } | null>(null);

  filterCustomerId: string | null = null;
  evaluateForm = {
    customerAccountId: '' as string | null,
    salesChannelId: '' as string | null,
    currencyCode: 'IRR',
    proposedAmount: 0
  };

  ngOnInit(): void {
    this.financeApi.listPlatformCustomers({ take: 500 }).subscribe({
      next: response => {
        const map = new Map<string, string>();
        for (const item of response.items ?? []) {
          map.set(item.id, `${item.displayName} #${item.customerCode}`);
        }
        this.customers.set(map);
      },
      error: () => this.customers.set(new Map())
    });
    this.api.listChannels().subscribe({ next: rows => this.channels.set(rows), error: () => this.channels.set([]) });
    this.loadDecisions();
  }

  customerLabel(customerId: string): string {
    return this.customers().get(customerId) ?? customerId.slice(0, 8);
  }

  channelOptions(): { label: string; value: string }[] {
    return this.channels().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  loadDecisions(): void {
    this.loading.set(true);
    this.listError.set('');
    this.api.listCreditDecisions(this.filterCustomerId ?? undefined).subscribe({
      next: rows => { this.decisions.set(rows); this.loading.set(false); },
      error: () => { this.decisions.set([]); this.listError.set(this.t('sales.credit.decisions.errorLoad')); this.loading.set(false); }
    });
  }

  runEvaluation(): void {
    if (!this.evaluateForm.customerAccountId) {
      this.evaluateError.set(this.t('sales.credit.evaluate.customerRequired'));
      return;
    }
    this.evaluating.set(true);
    this.evaluateError.set('');
    const payload: EvaluateCreditPayload = {
      customerAccountId: this.evaluateForm.customerAccountId,
      salesChannelId: this.evaluateForm.salesChannelId || null,
      currencyCode: this.evaluateForm.currencyCode,
      proposedAmount: this.evaluateForm.proposedAmount,
      correlationKey: `dashboard-${Date.now()}`
    };
    this.api.evaluateCredit(payload).subscribe({
      next: result => {
        this.lastResult.set({ allowed: result.allowed, availableCredit: result.availableCredit });
        this.evaluating.set(false);
        this.filterCustomerId = this.evaluateForm.customerAccountId;
        this.loadDecisions();
      },
      error: () => { this.evaluateError.set(this.t('sales.credit.evaluate.error')); this.evaluating.set(false); }
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
